import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import DashboardDetailLayout from './DashboardDetailLayout';
import ExecutionTimeline from './workflow/ExecutionTimeline';
import PerformanceMatrix from './workflow/PerformanceMatrix';
import PhaseBreakdown from './workflow/PhaseBreakdown';
import ArtifactOutput from './workflow/ArtifactOutput';
import ConfigConnections from './workflow/ConfigConnections';
import api from '../../services/api';

const WorkflowDetail: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState<any>(null);
  const [analytics, setAnalytics] = useState<any>(null);

  useEffect(() => {
    if (!workflowId) return;
    Promise.all([
      api.get(`/workflows/${workflowId}`).then(r => r.data).catch(() => null),
      api.get(`/analytics/cross-system/workflow/${workflowId}/detail`).then(r => r.data).catch(() => null),
    ]).then(([w, a]) => {
      setWorkflow(w);
      setAnalytics(a);
    });
  }, [workflowId]);

  if (!workflow || !workflowId) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
        Loading workflow...
      </div>
    );
  }

  const name = workflow.name || 'Unnamed Workflow';
  const phases = workflow.phases || [];

  return (
    <DashboardDetailLayout
      title={name}
      subtitle={workflow.description}
      backPath="/dashboards/workflows"
      badges={
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 text-xs rounded-full bg-blue-600/20 text-blue-300">{phases.length} phases</span>
          {analytics?.persona_count != null && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-purple-600/20 text-purple-300">{analytics.persona_count} personas</span>
          )}
        </div>
      }
      actions={
        <button
          onClick={() => navigate(`/workflows/${workflowId}`)}
          className="px-3 py-1.5 text-xs rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
        >
          Open in Designer
        </button>
      }
      tabs={[
        { key: 'timeline', label: 'Execution Timeline', content: <ExecutionTimeline workflowId={workflowId} analytics={analytics} /> },
        { key: 'matrix', label: 'Performance Matrix', content: <PerformanceMatrix workflowId={workflowId} analytics={analytics} /> },
        { key: 'phases', label: 'Phase Breakdown', content: <PhaseBreakdown phases={phases} /> },
        { key: 'artifacts', label: 'Artifact Output', content: <ArtifactOutput workflowId={workflowId} /> },
        { key: 'config', label: 'Configuration', content: <ConfigConnections workflow={workflow} /> },
      ]}
    />
  );
};

export default WorkflowDetail;
